import {Skeleton, Stack} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {format} from "date-fns";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {dibAdminApi} from "../../dibAdminApi/dibAdminApi";
import {getDateLocale} from "../../utils/dateLocales";
import {ResourceError} from "./ResourceError";
import {StatusMessage} from "./StatusMessage";
import type {DibDocument} from "./types";
import {renderNode} from "./renderNode";
import {useContentTitle} from "./useContentTitle";
import articleClasses from "./HtmlArticle.module.css";
import classes from "./NewsResource.module.css";

/** Children of a container entry (xdoc/xdesc), skipping inline text. */
const containerChildren = (doc: DibDocument, entries: {id: string}[]) =>
    entries.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children.filter((c): c is {id: string} => "id" in c);
    });

/**
 * Some news bodies open with an h1 repeating the topic name, which the
 * header already shows — drop it (and a wrapping <section> that only holds
 * it) so the title isn't rendered twice. Other leading headings are content.
 */
const renderBody = (doc: DibDocument) => {
    const children = containerChildren(doc, doc.root);
    const name = doc.name.trim();
    const isTitleEcho = (id: string): boolean => {
        const el = doc.elements[id];
        if (!el) return false;
        if (el.name === "h1") {
            const text = el.children
                .map((c) => ("text" in c ? c.text : ""))
                .join("")
                .trim();
            return text === name;
        }
        if (el.name === "section" && el.children.length === 1) {
            const only = el.children[0];
            return "id" in only && isTitleEcho(only.id);
        }
        return false;
    };
    const first = children[0];
    const body = first && isTitleEcho(first.id) ? children.slice(1) : children;
    return body.map((c) => renderNode(doc, c.id, {}, c.id));
};

const renderIngress = (doc: DibDocument) =>
    containerChildren(doc, doc.descroot ?? []).map((c) =>
        renderNode(doc, c.id, {}, c.id)
    );

const NewsSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={12} width="30%" mb="xs" />
        <Skeleton height={36} width="85%" />
        <Skeleton height={36} width="60%" mb="md" />
        <Skeleton height={16} width="100%" />
        <Skeleton height={16} width="95%" />
        <Skeleton height={16} width="70%" />
    </Stack>
);

/**
 * Renders a news topic (ResourceType.news): eyebrow with source and
 * publication date, title, ingress and the short body. The document only
 * carries `updatedAt`, so the date comes from the news feed entry. Follows the news-list design — same
 * eyebrow/title/ingress rhythm as the /news rows — rather than the
 * documentation layout (no TOC or side panel; news items are a few
 * paragraphs and an outbound link).
 */
export const NewsResource = ({resourceId}: {resourceId: string}) => {
    const {i18n} = useTranslation();
    const {
        currentData: doc,
        isFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({resourceId});
    const {data: newsItem} = dibAdminApi.endpoints.getNewsItem.useQuery(
        {topicId: resourceId, title: doc?.name ?? ""},
        {skip: !doc?.name}
    );

    useContentTitle(doc?.name);

    const getContent = () => {
        if (isFetching && !doc) return <NewsSkeleton />;
        if (isError) return <ResourceError error={error} />;
        if (!doc) {
            return (
                <StatusMessage
                    title="Empty document"
                    detail="This document has no content to display."
                />
            );
        }
        if (!doc.access) {
            return (
                <StatusMessage
                    title="Access denied"
                    detail="Your subscription does not include access to this document."
                />
            );
        }

        const pubDate = newsItem?.pubDate
            ? format(new Date(newsItem.pubDate), "d. MMMM yyyy", {
                  locale: getDateLocale(
                      i18n.resolvedLanguage ?? i18n.language ?? "nb"
                  ),
              })
            : null;
        const ingress = renderIngress(doc);
        const body = renderBody(doc);

        return (
            <>
                <header className={classes.header}>
                    <div className={classes.eyebrow}>
                        {doc.supplier && (
                            <span className={classes.sourcePill}>
                                {doc.supplier}
                            </span>
                        )}
                        {pubDate && (
                            <time
                                className={classes.eyebrowDate}
                                dateTime={newsItem?.pubDate ?? undefined}
                            >
                                {pubDate}
                            </time>
                        )}
                    </div>
                    <h1 className={classes.title}>{doc.name}</h1>
                    {ingress.length > 0 && (
                        <div className={classes.ingress}>{ingress}</div>
                    )}
                </header>
                {body.length > 0 && (
                    <div className={`${articleClasses.content} ${classes.body}`}>
                        {body}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={classes.page}>
            <article className={classes.article}>{getContent()}</article>
        </div>
    );
};
